import { useEffect, useState } from 'react'

// import * as fcl from '@onflow/fcl' // Commented out - Flow wallet replaced with MetaMask

import { useAuth } from 'providers/AuthProvider'
import {
  // claimCollectible,
  connectMagicWallet,
  getUserWallets,
  registerWallet
} from '../services/api.service'
import {
  createCrossmintWallet,
  getCrossmintWalletStatus,
  getCrossmintMyMoments,
  getCrossmintWalletBalance
} from '../services/crossmint.service'

// Magic SDK fully removed in favor of EVM wallets (Crossmint / MetaMask).
// The Magic-related state/methods kept below are stubs so the 16+ consumer
// files that still destructure `hasMagic` / `usingMagic` / `connectMagic` /
// `userAuth` / etc. continue to compile and behave as if no Magic user is
// present (which is also reality, since the Magic UI was removed earlier).
import { useOktaAuth } from '@okta/okta-react'
import { getEvmListingsByUser } from '../services/crossmint.marketplace.service'
import {
  getTotalListedMoments,
  getTotalNonListedMoments,
  getTotalMoments,
  getWUSDCBalanceForAccount
} from 'flow/helpers'
import useCollection from './useCollection'
import { RegisteredWallet } from 'types'
import { collectionUtils } from 'flow/helpers'

declare global {
  interface Window {
    ethereum?: {
      isMetaMask?: boolean
      isBraveWallet?: boolean
      providers?: Array<{
        isMetaMask?: boolean
        isBraveWallet?: boolean
        request: (args: { method: string; params?: unknown[] }) => Promise<any>
        on: (event: string, callback: (...args: any[]) => void) => void
        removeListener: (
          event: string,
          callback: (...args: any[]) => void
        ) => void
      }>
      request: (args: { method: string; params?: unknown[] }) => Promise<any>
      on: (event: string, callback: (...args: any[]) => void) => void
      removeListener: (
        event: string,
        callback: (...args: any[]) => void
      ) => void
    }
  }
}

function getMetaMaskProvider() {
  if (window.ethereum?.providers) {
    return (
      window.ethereum.providers.find((p) => p.isMetaMask && !p.isBraveWallet) ||
      null
    )
  }
  if (window.ethereum?.isMetaMask && !window.ethereum?.isBraveWallet) {
    return window.ethereum
  }
  return null
}

export default function useUserWallets() {
  const { authState, oktaAuth } = useOktaAuth()
  const { user } = useAuth()

  const [completedLoadingWalletsInfo, setCompletedLoadingWalletsInfo] =
    useState(false)
  const [registeredWallets, setRegisteredWallets] = useState<
    RegisteredWallet[]
  >([])
  const [lastUsedWallet, setLastUsedWallet] = useState(
    localStorage.getItem('lastUsedWallet') ?? 'unknown'
  )
  // Magic SDK has been removed — usingMagic must never become true again.
  // Initializing as `false` even when localStorage says `lastUsedWallet=magic`
  // is critical because several places do `usingMagic && magic.flow.authorization`
  // and the stub's `magic.flow` is `undefined` — accessing `.authorization`
  // on that would crash. Always start false; the rehydrate useEffect can't
  // flip it true either (the stubbed `magic.user.isLoggedIn()` returns false).
  const [usingMagic, setUsingMagic] = useState(false)
  const [usingMetaMask, setUsingMetaMask] = useState(
    lastUsedWallet === 'metamask'
  )

  const [flowUser, setFlowUser] = useState<any>()
  const [flowWUSDCBalance, setFlowWUSDCBalance] = useState(0)
  const [connectedWalletAddress, setConnectedWalletAddress] = useState('')

  const { loading, createCollection, collection } = useCollection(flowUser)

  const [hasMagic, setHasMagic] = useState(false)
  const [magicWalletWUSDCBalance, setMagicWalletWUSDCBalance] = useState(0)
  const [magicWalletAddress, setMagicWalletAddress] = useState('')
  const [magicMomentCount, setMagicMomentCount] = useState(0)
  const [magicForSaleMomentCount, setMagicForSaleMomentCount] = useState(0)
  const [magicNotForSaleMomentCount, setMagicNotForSaleMomentCount] =
    useState(0)

  // Blocto wallet integration has been removed. The migration script
  // (BE/scripts/migration/runBlocktoRecovery.ts) still exists for one-time
  // recovery of legacy Blocto users' assets to Base, but the FE no longer
  // exposes any Blocto-connect UX or state.

  // --- MetaMask state (replaces Flow/Blocto wallet) ---
  const [hasMetaMask, setHasMetaMask] = useState(false)
  const [metamaskWalletAddress, setMetamaskWalletAddress] = useState('')
  const [metamaskMomentCount] = useState(0)
  const [metamaskForSaleMomentCount] = useState(0)
  const [metamaskNotForSaleMomentCount] = useState(0)
  const [metamaskWalletBalance] = useState(0)

  // --- Crossmint state ---
  const [hasCrossmint, setHasCrossmint] = useState(false)
  const [crossmintWalletAddress, setCrossmintWalletAddress] = useState('')
  const [crossmintProvisioned, setCrossmintProvisioned] = useState(false)
  const [usingCrossmint, setUsingCrossmint] = useState(
    lastUsedWallet === 'crossmint'
  )
  const [crossmintMomentCount, setCrossmintMomentCount] = useState(0)
  const [crossmintForSaleMomentCount, setCrossmintForSaleMomentCount] =
    useState(0)
  const [crossmintNotForSaleMomentCount, setCrossmintNotForSaleMomentCount] =
    useState(0)
  const [crossmintWalletBalance, setCrossmintWalletBalance] = useState(0)
  const [crossmintBalanceRefreshing, setCrossmintBalanceRefreshing] =
    useState(false)

  const [isConnected, setIsConnected] = useState(false)
  const [userMetadata, setUserMetadata] = useState({})
  const [isExporting, setIsExporting] = useState(false)
  const [exportError, setExportError] = useState<string | null>(null)

  // Magic SDK stub. All methods are no-ops that throw to make any accidental
  // invocation observable rather than silently succeed. The fields shape
  // matches what the rest of the file references so TypeScript is happy.
  const magic = {
    user: {
      isLoggedIn: async () => false,
      getMetadata: async () => ({ publicAddress: '' }),
      logout: async () => undefined
    },
    auth: {
      loginWithEmailOTP: async () =>
        Promise.reject(new Error('Magic wallet removed')),
      loginWithOTP: async () =>
        Promise.reject(new Error('Magic wallet removed'))
    },
    openid: {
      loginWithOIDC: async () =>
        Promise.reject(new Error('Magic wallet removed'))
    },
    wallet: {
      showUI: async () => Promise.reject(new Error('Magic wallet removed'))
    },
    flow: undefined as undefined | { authorization: unknown }
  }

  // --- MetaMask: listen for account/chain changes ---
  useEffect(() => {
    const provider = getMetaMaskProvider()
    if (!provider) return

    const handleAccountsChanged = (accounts: string[]) => {
      if (accounts.length === 0) {
        setIsConnected(false)
        setMetamaskWalletAddress('')
        setHasMetaMask(false)
        setConnectedWalletAddress('')
      } else if (usingMetaMask) {
        const account = accounts[0]
        setMetamaskWalletAddress(account)
        setConnectedWalletAddress(account)
      }
    }

    const handleChainChanged = () => {
      if (usingMetaMask) {
        window.location.reload()
      }
    }

    provider.on('accountsChanged', handleAccountsChanged)
    provider.on('chainChanged', handleChainChanged)

    return () => {
      provider.removeListener('accountsChanged', handleAccountsChanged)
      provider.removeListener('chainChanged', handleChainChanged)
    }
  }, [usingMetaMask])

  // --- MetaMask: restore connection on load ---
  useEffect(() => {
    const provider = getMetaMaskProvider()
    if (lastUsedWallet === 'metamask' && provider) {
      provider
        .request({ method: 'eth_accounts' })
        .then((accounts: string[]) => {
          if (accounts.length > 0) {
            const account = accounts[0]
            setIsConnected(true)
            setUsingMetaMask(true)
            setUsingMagic(false)
            setHasMetaMask(true)
            setMetamaskWalletAddress(account)
            setConnectedWalletAddress(account)
          }
        })
        .catch(console.error)
    }
  }, [])

  // --- Crossmint: restore connection on load ---
  useEffect(() => {
    const savedCrossmintAddress = localStorage.getItem('crossmintWalletAddress')
    if (lastUsedWallet === 'crossmint' && savedCrossmintAddress) {
      setIsConnected(true)
      setUsingCrossmint(true)
      setUsingMagic(false)
      setUsingMetaMask(false)
      setHasCrossmint(true)
      setCrossmintWalletAddress(savedCrossmintAddress)
      setConnectedWalletAddress(savedCrossmintAddress)
    }
  }, [])

  const checkAndUpdateUsersRegisteredWallets = async () => {
    if (!user?.sub) return

    const registeredWallets: RegisteredWallet[] = await getUserWallets(
      user?.sub
    )
    setRegisteredWallets(registeredWallets)

    const magicWallets = registeredWallets.filter(
      (wallet: any) => wallet.isMagic
    )

    const totalMagicWallets = magicWallets.length
    const userHasMagicWallet = totalMagicWallets > 0

    if (userHasMagicWallet && magicWallets.length)
      setMagicWalletAddress(magicWallets[0].walletAddress)

    setHasMagic(userHasMagicWallet)

    // Restore MetaMask state from localStorage
    const savedMetaMaskAddress = localStorage.getItem('metamaskWalletAddress')
    if (savedMetaMaskAddress) {
      setHasMetaMask(true)
      setMetamaskWalletAddress(savedMetaMaskAddress)
    }

    await refreshWallets()
    setCompletedLoadingWalletsInfo(true)
  }

  useEffect(() => {
    if (user?.sub) checkAndUpdateUsersRegisteredWallets()
  }, [user, user?.sub, connectedWalletAddress])

  // updates user wallet selection (Magic only — Blocto/FCL removed)
  useEffect(() => {
    if (usingMagic) {
      try {
        magic.user.isLoggedIn().then(async (magicIsLoggedIn) => {
          setIsConnected(magicIsLoggedIn)
          if (magicIsLoggedIn) {
            const { publicAddress } = await magic.user.getMetadata()
            if (publicAddress) {
              setFlowUser({ addr: publicAddress })
              setConnectedWalletAddress(publicAddress)
              setMagicWalletAddress(publicAddress)
              setHasMagic(true)
              await registerWallet(publicAddress, user?.sub || '', true)
            }
            setUserMetadata(await magic.user.getMetadata())
            localStorage.setItem('lastUsedWallet', 'magic')
            setLastUsedWallet('magic')
          }
        })
      } catch (err) {
        console.error(err)
      }
    }
    // --- Commented out: Blocto/FCL subscription ---
    // else {
    //   fcl.currentUser().subscribe(async (fclUser: any) => {
    //     if (fclUser?.addr) {
    //       setIsConnected(true)
    //       setFlowUser(fclUser)
    //       setUsingMagic(false)
    //       setBloctoWalletAddress(fclUser.addr)
    //       setConnectedWalletAddress(fclUser.addr)
    //       setHasBlocto(true)
    //       localStorage.setItem('lastUsedWallet', 'blocto')
    //       setLastUsedWallet('blocto')
    //       await registerWallet(fclUser.addr, user?.sub || '', false)
    //     } else {
    //       setFlowUser(undefined)
    //       setConnectedWalletAddress('')
    //     }
    //   })
    // }
  }, [authState, usingMagic])

  useEffect(() => {
    const registerAndCheckWallets = async () => {
      if (flowUser?.addr && user?.sub) {
        getFlowWUSDCBalance()
      } else {
        setFlowWUSDCBalance(0)
        return
      }
    }
    registerAndCheckWallets()
    window.addEventListener('focus', getFlowWUSDCBalance)
    return () => {
      window.removeEventListener('focus', getFlowWUSDCBalance)
    }
  }, [flowUser?.addr, user, user?.sub, usingMagic])

  // --- Magic wallet login (REMOVED) ---
  // Magic SDK has been removed from the app. This function is kept as a
  // no-op so context consumers (`connectMagic`) don't break, but it now
  // immediately rejects so any accidental invocation is observable.
  const loginMagic = async () => {
    console.warn(
      '[useUserWallets] Magic wallet has been removed. Use Crossmint or MetaMask instead.'
    )
    return Promise.reject(new Error('Magic wallet removed'))
  }

  const logoutMagic = async () => {
    // Magic SDK removed — no-op. Reset local state so any lingering
    // `usingMagic === true` flags get cleared if reached.
    setUsingMagic(false)
    setHasMagic(false)
    setMagicWalletAddress('')
    setIsConnected(false)
  }

  // --- Commented out: Blocto/Flow authentication ---
  // const authenticateBlocto = async () => {
  //   const authPromise: () => Promise<void> = () =>
  //     new Promise((resolve) => {
  //       fcl
  //         .authenticate()
  //         .then(async (status: any) => {
  //           if (status.loggedIn) {
  //             setUsingMagic(false)
  //             localStorage.setItem('lastUsedWallet', 'blocto')
  //             localStorage.setItem('lastUsedBloctoAddress', status.addr)
  //             setLastUsedWallet('blocto')
  //             setHasBlocto(true)
  //             setIsConnected(true)
  //             await registerWallet(status.addr, user?.sub || '', false)
  //           }
  //         })
  //         .finally(async () => {
  //           await checkAndUpdateUsersRegisteredWallets()
  //           resolve()
  //         })
  //     })
  //   await authPromise()
  // }

  // --- MetaMask connection ---
  const connectMetaMask = async (): Promise<{
    success: boolean
    error?: string
  }> => {
    const provider = getMetaMaskProvider()
    if (!provider) {
      return { success: false, error: 'not_installed' }
    }

    try {
      const accounts: string[] = await provider.request({
        method: 'eth_requestAccounts'
      })

      if (accounts.length > 0) {
        const account = accounts[0]
        setIsConnected(true)
        setUsingMetaMask(true)
        setUsingMagic(false)
        setHasMetaMask(true)
        setMetamaskWalletAddress(account)
        setConnectedWalletAddress(account)
        localStorage.setItem('lastUsedWallet', 'metamask')
        localStorage.setItem('metamaskWalletAddress', account)
        setLastUsedWallet('metamask')
        return { success: true }
      }
      return { success: false, error: 'no_accounts' }
    } catch (error: any) {
      console.error('MetaMask connection error:', error)
      return {
        success: false,
        error: error?.message || 'Connection failed'
      }
    }
  }

  const disconnectMetaMask = () => {
    setIsConnected(false)
    setUsingMetaMask(false)
    setConnectedWalletAddress('')
    localStorage.removeItem('metamaskWalletAddress')
  }

  // --- Crossmint connection ---
  const connectCrossmint = async (): Promise<{
    success: boolean
    error?: string
    walletAddress?: string
  }> => {
    try {
      const { walletAddress } = await createCrossmintWallet()
      setIsConnected(true)
      setUsingCrossmint(true)
      setUsingMagic(false)
      setUsingMetaMask(false)
      setHasCrossmint(true)
      setCrossmintWalletAddress(walletAddress)
      setConnectedWalletAddress(walletAddress)
      setCrossmintProvisioned(true)
      localStorage.setItem('lastUsedWallet', 'crossmint')
      localStorage.setItem('crossmintWalletAddress', walletAddress)
      setLastUsedWallet('crossmint')
      if (user?.sub) {
        await registerWallet(walletAddress, user.sub, false)
        await checkAndUpdateUsersRegisteredWallets()
      }
      return { success: true, walletAddress }
    } catch (error: any) {
      console.error('Crossmint wallet creation error:', error)
      return {
        success: false,
        error: error?.message || 'Failed to create Crossmint wallet'
      }
    }
  }

  const disconnectCrossmint = () => {
    setIsConnected(false)
    setUsingCrossmint(false)
    setHasCrossmint(false)
    setCrossmintWalletAddress('')
    setConnectedWalletAddress('')
    localStorage.removeItem('crossmintWalletAddress')
  }

  const getMagicIds = async () => {
    if (!magicWalletAddress) return
    setMagicMomentCount(await getTotalMoments(magicWalletAddress))
    setMagicForSaleMomentCount(await getTotalListedMoments(magicWalletAddress))
    setMagicNotForSaleMomentCount(
      await getTotalNonListedMoments(magicWalletAddress)
    )
  }

  const getCrossmintIds = async () => {
    const result = await getCrossmintMyMoments()
    if (!result) return
    const listingsResult = crossmintWalletAddress
      ? await getEvmListingsByUser(crossmintWalletAddress).catch(() => null)
      : null
    // Mirror the same type filter useUsersNFTs applies for the Collectibles tab:
    // only count moments and club-badges (not rewards or packs).
    const collectibles = result.moments.filter((m) => {
      const type = m.templateData?.immutableData?.type
      return !type || type === 'moment' || type === 'club-badge'
    })

    const forSaleCount = listingsResult?.listings?.length || 0

    setCrossmintMomentCount(collectibles.length + forSaleCount)
    setCrossmintForSaleMomentCount(
      collectibles.filter((m) => m.forSale).length + forSaleCount
    )
    setCrossmintNotForSaleMomentCount(
      collectibles.filter((m) => !m.forSale).length
    )
  }

  const getMagicWUSDCBalance = async () => {
    if (!magicWalletAddress) return
    const res = await getWUSDCBalanceForAccount(magicWalletAddress)
    setMagicWalletWUSDCBalance(res ?? 0)
  }

  const refreshWallets = async () => {
    await getMagicIds()
    await getMagicWUSDCBalance()
  }

  useEffect(() => {
    if (magicWalletAddress) {
      getMagicIds()
      getMagicWUSDCBalance()
    }
  }, [magicWalletAddress])

  useEffect(() => {
    if (crossmintWalletAddress) {
      getCrossmintIds()
      getCrossmintWalletBalance().then(setCrossmintWalletBalance)
      // Poll every 30 s so the balance stays current without a manual refresh
      const interval = setInterval(() => {
        getCrossmintWalletBalance().then(setCrossmintWalletBalance)
      }, 30000)
      return () => clearInterval(interval)
    }
  }, [crossmintWalletAddress])

  const refreshCrossmintBalance = () =>
    getCrossmintWalletBalance().then(setCrossmintWalletBalance)

  const refreshCrossmintBalanceWithPolling = async (
    previousBalance: number
  ) => {
    setCrossmintBalanceRefreshing(true)
    // Poll immediately then every 3 s for up to 60 s waiting for balance to change
    const MAX_POLLS = 20
    for (let i = 0; i < MAX_POLLS; i++) {
      if (i > 0) await new Promise((resolve) => setTimeout(resolve, 3000))
      try {
        const newBal = await getCrossmintWalletBalance()
        setCrossmintWalletBalance(newBal)
        if (Math.abs(newBal - previousBalance) > 0.001) break
      } catch {
        // keep polling
      }
    }
    setCrossmintBalanceRefreshing(false)
  }

  const handleConnect = async () => {
    if (usingMagic) {
      await loginMagic()
    } else {
      await connectMetaMask()
    }
  }

  const handleDisconnect = () => {
    if (usingMagic) {
      logoutMagic()
    } else if (usingMetaMask) {
      disconnectMetaMask()
    } else if (usingCrossmint) {
      disconnectCrossmint()
    }
  }

  const getFlowWUSDCBalance = async () => {
    if (!flowUser?.addr) return
    try {
      const res = await getWUSDCBalanceForAccount(flowUser?.addr)
      setFlowWUSDCBalance(Number(res ?? 0))
      if (usingMagic) {
        setMagicWalletWUSDCBalance(Number(res ?? 0))
      }
    } catch (err) {
      console.error(err)
      setFlowWUSDCBalance(0)
    }
  }

  const showMagicWalletUI = async () => {
    try {
      await magic.wallet.showUI()
      const magicIsLoggedIn = await magic.user.isLoggedIn()
      if (!magicIsLoggedIn) {
        setIsConnected(false)
      }
    } catch (error) {
      if (
        (error as Error).message ===
        'Magic RPC Error: [-32603] User rejected the action'
      ) {
        console.info('User dismissed the modal')
      } else {
        console.error('error from showUI', error)
      }
    }
  }

  const exportMagicWalletPK = async () => {
    setIsExporting(true)
    setExportError(null)
    try {
      const token = await oktaAuth.tokenManager.get('idToken')

      if (!token?.idToken) {
        console.error('Failed to obtain fresh OIDC JWT')
        setExportError(
          'Could not get authentication token. Please try logging in again.'
        )
        return
      }

      const idToken = token.idToken
      const revealUrl = `https://reveal.magic.link/${
        import.meta.env.VITE_MAGIC_APP_NAME
      }?token=${encodeURIComponent(idToken)}`

      window.open(revealUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error(
        'Error obtaining fresh JWT or opening Magic reveal page:',
        error
      )
      setExportError('An error occurred during export. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  return {
    registeredWallets,
    flowUser,
    hasMagic,
    hasMetaMask,
    hasCrossmint,
    magicWalletAddress,
    metamaskWalletAddress,
    crossmintWalletAddress,
    magicMomentCount,
    metamaskMomentCount,
    connectedWalletAddress,
    isConnected,
    tools: {
      connect: handleConnect,
      disconnect: handleDisconnect
    },
    connectMetaMask,
    connectCrossmint,
    connectMagic: loginMagic,
    flowWUSDCBalance,
    magicWalletWUSDCBalance,
    metamaskWalletBalance,
    getFlowWUSDCBalance,
    magic,
    userAuth: usingMagic ? magic.flow?.authorization : null,
    userMetadata,
    usingMagic,
    usingMetaMask,
    usingCrossmint,
    crossmintProvisioned,
    setUsingMagic,
    completedLoadingWalletsInfo,
    collection,
    collectionIsLoading: loading,
    createCollection,
    lastUsedWallet,
    checkAndUpdateUsersRegisteredWallets,
    refreshWallets,
    magicForSaleMomentCount,
    metamaskForSaleMomentCount,
    magicNotForSaleMomentCount,
    metamaskNotForSaleMomentCount,
    crossmintMomentCount,
    crossmintForSaleMomentCount,
    crossmintNotForSaleMomentCount,
    crossmintWalletBalance,
    crossmintBalanceRefreshing,
    refreshCrossmintBalance,
    refreshCrossmintBalanceWithPolling,
    showMagicWalletUI,
    exportMagicWalletPK,
    isExporting,
    exportError
  }
}
